import React, { useState } from 'react';
import { Copy, Thermometer, Umbrella, Wind, TrendingDown, Clock, ArrowDown, ArrowUp } from 'lucide-react';
import { ClothingRecommendation, TemperatureSensitivity } from '../types';
import { getWeatherEmoji, getWindDescription, getWindDirection, getTimeString } from '../utils/weather';

interface WeatherResultProps {
  temperature: number;
  currentTemperature?: number;
  feelsLike?: number;
  minTemperature?: number;
  recommendation: ClothingRecommendation;
  weatherCode?: number;
  precipitationProbability?: number;
  windSpeed?: number;
  windDirection?: number;
  onSensitivityChange: (sensitivity: TemperatureSensitivity) => void;
  currentSensitivity: TemperatureSensitivity;
  hourlyTemps?: { time: string; temp: number }[];
  yesterdayTemp?: number;
}

export const WeatherResult: React.FC<WeatherResultProps> = ({ 
  temperature,
  currentTemperature,
  feelsLike,
  minTemperature,
  recommendation,
  weatherCode,
  precipitationProbability,
  windSpeed,
  windDirection,
  onSensitivityChange,
  currentSensitivity,
  hourlyTemps,
  yesterdayTemp
}) => {
  const [copied, setCopied] = useState(false);

  const copyRecommendation = () => {
    const text = `היי! הנה המלצת הלבוש שלי להיום:
🌡️ ${currentTemperature !== undefined ? `${currentTemperature.toFixed(1)}°` : `${temperature.toFixed(1)}°`}${feelsLike ? ` (מרגיש כמו ${feelsLike.toFixed(1)}°)` : ''}
${recommendation.emoji}
${recommendation.text}
${recommendation.items.map(item => `• ${item}`).join('\n')}
${precipitationProbability ? `☔️ ${precipitationProbability}% סיכוי לגשם` : ''}
${windSpeed ? `💨 ${getWindDescription(windSpeed)}` : ''}`;

    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const tempDrop = minTemperature !== undefined ? temperature - minTemperature : 0;
  const showTempDrop = tempDrop > 3;

  const findTempDrops = () => {
    if (!hourlyTemps) return null;

    const drops: { time: string; drop: number }[] = [];
    for (let i = 0; i < hourlyTemps.length - 1; i++) {
      const currentTemp = hourlyTemps[i].temp;
      const nextTemp = hourlyTemps[i + 1].temp;
      const drop = currentTemp - nextTemp;
      
      if (drop >= 2) {
        drops.push({
          time: hourlyTemps[i + 1].time,
          drop: drop
        });
      }
    }
    return drops.length > 0 ? drops : null;
  };

  const tempDrops = findTempDrops();

  const getComparisonText = () => {
    if (yesterdayTemp === undefined) return null;
    
    const tempToCompare = currentTemperature ?? temperature;
    const diff = tempToCompare - yesterdayTemp;
    const absDiff = Math.abs(diff);

    if (absDiff <= 0.1) return { text: 'זהה', icon: null };
    if (absDiff <= 1.5) return { text: 'דומה', icon: null };
    if (diff > 1.5) return { text: 'חם יותר', icon: <ArrowUp className="text-orange-500" size={16} /> };
    return { text: 'קר יותר', icon: <ArrowDown className="text-blue-500" size={16} /> };
  };

  const comparison = getComparisonText();

  return (
    <div className="relative">
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
        {/* Temperature and Weather Display */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Thermometer className="text-slate-400" size={24} />
            <div>
              <div className="text-4xl font-bold text-slate-800">
                {currentTemperature !== undefined 
                  ? `${currentTemperature.toFixed(1)}°`
                  : `${temperature.toFixed(1)}°`
                }
              </div>
              {feelsLike !== undefined && (
                <div className="text-sm text-slate-500">
                  מרגיש כמו: {feelsLike.toFixed(1)}°
                </div>
              )}
              {currentTemperature !== undefined && (
                <div className="text-sm text-slate-500">
                  מקסימום: {temperature.toFixed(1)}°
                </div>
              )}
              {minTemperature !== undefined && (
                <div className="text-sm text-slate-500">
                  מינימום: {minTemperature.toFixed(1)}°
                </div>
              )}
              {comparison && (
                <div className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                  ביחס לאתמול: 
                  <span className="flex items-center gap-1">
                    {comparison.text}
                    {comparison.icon}
                  </span>
                </div>
              )}
            </div>
          </div>
          {weatherCode !== undefined && (
            <div className="text-4xl">
              {getWeatherEmoji(weatherCode)}
            </div>
          )}
        </div>

        {/* Temperature Sensitivity Selector */}
        <div className="mb-6 bg-slate-50 p-4 rounded-xl border border-slate-200">
          <div className="text-base font-medium text-slate-700 mb-3 text-center">
            איך אתם מרגישים בדרך כלל?
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => onSensitivityChange('cold')}
              className={`flex-1 py-3 px-3 rounded-lg text-base font-medium transition-all ${
                currentSensitivity === 'cold'
                  ? 'bg-blue-500 text-white shadow-lg shadow-blue-200'
                  : 'bg-white text-slate-600 hover:bg-blue-50 border border-slate-200'
              }`}
            >
              קר לי בקלות ❄️
            </button>
            <button
              onClick={() => onSensitivityChange('default')}
              className={`flex-1 py-3 px-3 rounded-lg text-base font-medium transition-all ${
                currentSensitivity === 'default'
                  ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-200'
                  : 'bg-white text-slate-600 hover:bg-emerald-50 border border-slate-200'
              }`}
            >
              רגיל 😊
            </button>
            <button
              onClick={() => onSensitivityChange('hot')}
              className={`flex-1 py-3 px-3 rounded-lg text-base font-medium transition-all ${
                currentSensitivity === 'hot'
                  ? 'bg-orange-500 text-white shadow-lg shadow-orange-200'
                  : 'bg-white text-slate-600 hover:bg-orange-50 border border-slate-200'
              }`}
            >
              חם לי בקלות 🌡️
            </button>
          </div>
        </div>

        {/* Recommendation Display */}
        <div className="text-center space-y-4 mb-6 bg-slate-50 p-4 rounded-xl">
          <div className="text-xl font-medium text-slate-700">
            {recommendation.text}
          </div>
          <div className="text-right space-y-2">
            {recommendation.items.map((item, index) => (
              <div 
                key={index}
                className="text-slate-600"
              >
                {item}
              </div>
            ))}
          </div>
        </div>

        {/* Temperature Change Warning */}
        {(showTempDrop || tempDrops) && (
          <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
            <div className="flex items-center gap-2 text-blue-700 mb-2">
              <TrendingDown size={20} />
              <span className="font-medium">צפויה ירידת טמפרטורה!</span>
            </div>
            <div className="space-y-2">
              {showTempDrop && (
                <p className="text-blue-600 text-sm">
                  הטמפרטורה צפויה לרדת ב-{tempDrop.toFixed(1)}° מעלות במהלך היום.
                </p>
              )}
              {tempDrops && tempDrops.map((drop, index) => (
                <div key={index} className="flex items-center gap-2 text-blue-600 text-sm">
                  <Clock size={16} />
                  <span>
                    ירידה של {drop.drop.toFixed(1)}° מעלות בשעה {getTimeString(drop.time)}
                  </span>
                </div>
              ))}
              <p className="text-blue-700 text-sm font-medium mt-2">
                🧥 מומלץ לקחת שכבה נוספת!
              </p>
            </div>
          </div>
        )}

        {/* Weather Info Cards */}
        <div className="grid gap-3 mb-6">
          {precipitationProbability !== undefined && (
            <div className={`p-3 rounded-lg flex items-center gap-3 
              ${precipitationProbability > 30 
                ? 'bg-blue-50 text-blue-700' 
                : 'bg-slate-50 text-slate-600'}`}
            >
              <Umbrella size={20} />
              <div>
                <span>{precipitationProbability}% סיכוי לגשם</span>
                {precipitationProbability > 30 && (
                  <span className="font-medium mr-2">
                    - קח מטריה!
                  </span>
                )}
              </div>
            </div>
          )}

          {windSpeed !== undefined && (
            <div className="p-3 bg-slate-50 rounded-lg flex items-center gap-3 text-slate-600">
              <Wind size={20} />
              <div>
                <span>{getWindDescription(windSpeed)}</span>
                {windDirection !== undefined && (
                  <span className="mr-1 text-slate-500">
                    מכיוון {getWindDirection(windDirection)}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Copy Button */}
        <button
          onClick={copyRecommendation}
          className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-slate-50 text-slate-600 rounded-lg hover:bg-slate-100 transition-colors relative"
        >
          <Copy size={18} />
          <span>{copied ? 'ההמלצה הועתקה!' : 'העתק המלצה'}</span>
          {copied && (
            <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 bg-emerald-500 text-white text-xs py-1 px-2 rounded-full">
              ✓ הועתק
            </div>
          )}
        </button>
      </div>
    </div>
  );
};